import { useCallback, useMemo } from 'react';

import { Permission } from '@/entities/permissions';
import { usePermission } from '@/features/_shared/user';
import { useCsvDownloadContext } from '@/features/charts/context/CsvTableContext';
import { useAppDispatch } from '@/store/hooks';
import { openExportDrawer } from '@/store/slices/drawer.slice';
import { setExport } from '@/store/slices/export.slice';
import { ExportSelectionSource } from '../../hooks/useOpenExportSelection';
import type { CsvWidgetExportSource } from '../../types/context.types';

export function useCsvWidgetExportAction(options: { tabName?: string }): {
  disabled: boolean;
  exportCsv: () => void;
} {
  const { tabName } = options;
  const { hasPermission } = usePermission();
  const { downloadFileId, selectedRange, fileName, groupName, dateColumnKey } =
    useCsvDownloadContext();
  const dispatch = useAppDispatch();
  const source = useMemo<CsvWidgetExportSource | null>(() => {
    if (downloadFileId) {
      return { sourceType: 'query', queryFileId: downloadFileId, selectedRange };
    }
    if (fileName && groupName) {
      return { sourceType: 'datalake', fileName, groupName };
    }
    return null;
  }, [downloadFileId, selectedRange, fileName, groupName]);

  const exportCsv = useCallback(() => {
    if (!source) return;

    dispatch(
      setExport({
        selectedTimeRange:
          source.sourceType === 'query' ? (source.selectedRange ?? undefined) : undefined
      })
    );
    dispatch(
      openExportDrawer({
        showHistory: false,
        source: ExportSelectionSource.WIDGET_BUTTON,
        csvWidgetExportContext: { source, dateColumnKey: dateColumnKey ?? null, tabName }
      })
    );
  }, [dateColumnKey, dispatch, source, tabName]);

  return {
    disabled: !hasPermission(Permission.ExportBasic) || source === null,
    exportCsv
  };
}
